import { Checkbox, Stack } from '@mantine/core';
import { BasicFieldInputs } from '../components/BasicFieldInputs';
import { useIntl } from 'react-intl';
import { toInputName } from '../utils';

export const TextField = ({
  field,
  setField,
}: {
  field: any;
  setField: (field: any) => void;
}) => {
  const intl = useIntl();
  const fieldName = field.name || toInputName(field.label || '');
  const isDiacriticsEnabled = (field.diacritics || []).includes(fieldName);
  const isAutoCorrectEnabled = (field.autoCorrect || []).includes(fieldName);

  return (
    <Stack gap="md">
      <BasicFieldInputs
        fields={['label', 'description']}
        fieldValues={field}
        requiredFields={['label']}
        onChange={(key, value) => {
          const updatedField = { ...field, [key]: value };
          // Update name when label changes
          if (key === 'label') {
            updatedField.name = toInputName(value);
            // Update diacritics array if it was enabled (only for text fields)
            if (field.type === 'text' && isDiacriticsEnabled) {
              const oldName = fieldName;
              const newName = toInputName(value);
              updatedField.diacritics = field.diacritics
                ? field.diacritics.map((d: string) =>
                    d === oldName ? newName : d
                  )
                : [newName];
            }
            // Update autoCorrect array if it was enabled (only for textarea fields)
            if (field.type === 'textarea' && isAutoCorrectEnabled) {
              const oldName = fieldName;
              const newName = toInputName(value);
              updatedField.autoCorrect = field.autoCorrect
                ? field.autoCorrect.map((a: string) =>
                    a === oldName ? newName : a
                  )
                : [newName];
            }
          }
          setField(updatedField);
        }}
      />

      {field.type === 'text' && (
        <Checkbox
          label={intl.formatMessage({ id: 'diacriticsFields' })}
          checked={isDiacriticsEnabled}
          onChange={(event) => {
            const currentDiacritics = field.diacritics || [];
            if (event.currentTarget.checked) {
              // Add field name to diacritics array
              if (!currentDiacritics.includes(fieldName)) {
                setField({
                  ...field,
                  diacritics: [...currentDiacritics, fieldName],
                });
              }
            } else {
              // Remove field name from diacritics array
              setField({
                ...field,
                diacritics: currentDiacritics.filter(
                  (d: string) => d !== fieldName
                ),
              });
            }
          }}
        />
      )}

      {field.type === 'textarea' && (
        <Checkbox
          label={intl.formatMessage({ id: 'autoCorrectFields' })}
          checked={isAutoCorrectEnabled}
          onChange={(event) => {
            const currentAutoCorrect = field.autoCorrect || [];
            if (event.currentTarget.checked) {
              // Add field name to autoCorrect array
              if (!currentAutoCorrect.includes(fieldName)) {
                setField({
                  ...field,
                  autoCorrect: [...currentAutoCorrect, fieldName],
                });
              }
            } else {
              // Remove field name from autoCorrect array
              setField({
                ...field,
                autoCorrect: currentAutoCorrect.filter(
                  (a: string) => a !== fieldName
                ),
              });
            }
          }}
        />
      )}

      <Checkbox
        label={intl.formatMessage({ id: 'disabled' })}
        value={field.isDisabled}
        onChange={(event) =>
          setField({ ...field, isDisabled: event.currentTarget.checked })
        }
      />
    </Stack>
  );
};
